import { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Home, 
  Users, 
  UserCheck,
  Settings, 
  BarChart3, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  CalendarDays,
  MessageSquare,
  Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalListings: 0,
    available: 0,
    pending: 0,
    sold: 0,
    totalEmployees: 0
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const [listingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/reports/listings-summary`),
        axios.get(`${API}/users/employees`)
      ]);
      
      setStats({
        totalListings: listingsRes.data.total_listings,
        available: listingsRes.data.by_status?.available || 0,
        pending: listingsRes.data.by_status?.pending || 0,
        sold: listingsRes.data.by_status?.sold || 0,
        totalEmployees: usersRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
    { path: '/admin/listings', label: 'Listings', icon: Home },
    { path: '/admin/schedule', label: 'Schedule', icon: CalendarDays },
    { path: '/admin/inquiries', label: 'Inquiries', icon: MessageSquare },
    { path: '/admin/customers', label: 'Customers', icon: UserCheck },
    { path: '/admin/employees', label: 'Employees', icon: Users },
    { path: '/admin/roles', label: 'Team Roles', icon: Shield },
    { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // If we're at the base /admin route, show the overview
  const showOverview = location.pathname === '/admin';

  return (
    <div className="flex h-screen overflow-hidden bg-secondary/30">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-brand-brown text-white transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_property-mgmt-43/artifacts/8gx78s2p_Signe%20logo.jpg" alt="Signé" className="h-10 w-10 rounded-full object-cover" />
              <span className="font-heading text-lg font-bold tracking-tight">Signé <span className="text-brand-gold">Properties</span></span>
            </Link>
            <p className="font-body text-xs text-white/50 mt-1">Admin Dashboard</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 font-heading text-sm uppercase tracking-wide transition-colors
                  ${isActive(item.path, item.exact) 
                    ? 'bg-white/10 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/5'}
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-2 mb-4">
              <div className="w-10 h-10 bg-brand-gold flex items-center justify-center font-heading font-bold text-brand-brown">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-sm font-semibold truncate">{user?.name}</p>
                <p className="font-body text-xs text-white/50 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start gap-3 px-4 py-3 font-heading text-sm uppercase tracking-wide text-white/60 hover:text-white hover:bg-white/5 rounded-none"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-secondary rounded-none"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/admin" className="hover:text-foreground">Dashboard</Link>
              {location.pathname !== '/admin' && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-foreground capitalize">
                    {location.pathname.split('/').pop()}
                  </span>
                </>
              )}
            </div>
          </div>
          <Link to="/">
            <Button variant="outline" className="rounded-none border-2 font-heading text-xs uppercase tracking-wide" data-testid="view-site-btn">
              View Site
            </Button>
          </Link>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {showOverview ? (
            <div className="space-y-8 animate-fade-in">
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                  Welcome back, {user?.name}
                </h1>
                <p className="font-body text-muted-foreground mt-1">
                  Here's what's happening with your properties today.
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white p-6 border border-border hover-lift">
                  <p className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Total Listings
                  </p>
                  <p className="font-heading text-3xl font-bold text-foreground mt-2" data-testid="stat-total-listings">
                    {stats.totalListings}
                  </p>
                </div>
                <div className="bg-white p-6 border border-border hover-lift">
                  <p className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Available
                  </p>
                  <p className="font-heading text-3xl font-bold text-foreground mt-2" data-testid="stat-available">
                    {stats.available}
                  </p>
                </div>
                <div className="bg-white p-6 border border-border hover-lift">
                  <p className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Pending
                  </p>
                  <p className="font-heading text-3xl font-bold text-yellow-600 mt-2" data-testid="stat-pending">
                    {stats.pending}
                  </p>
                </div>
                <div className="bg-white p-6 border border-border hover-lift">
                  <p className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Sold
                  </p>
                  <p className="font-heading text-3xl font-bold text-brand-gold mt-2" data-testid="stat-sold">
                    {stats.sold}
                  </p>
                </div>
                <div className="bg-white p-6 border border-border hover-lift">
                  <p className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Employees
                  </p>
                  <p className="font-heading text-3xl font-bold text-foreground mt-2" data-testid="stat-employees">
                    {stats.totalEmployees}
                  </p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link to="/admin/listings" className="block">
                  <div className="bg-brand-brown text-white p-8 hover-lift cursor-pointer group">
                    <Home className="w-8 h-8 mb-4 text-brand-gold" />
                    <h3 className="font-heading text-lg font-semibold mb-2">Manage Listings</h3>
                    <p className="font-body text-sm text-white/60">Add, edit, or assign properties</p>
                    <ChevronRight className="w-5 h-5 mt-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </Link>
                <Link to="/admin/employees" className="block">
                  <div className="bg-white border border-border p-8 hover-lift cursor-pointer group">
                    <Users className="w-8 h-8 mb-4 text-brand-gold" />
                    <h3 className="font-heading text-lg font-semibold mb-2">Manage Team</h3>
                    <p className="font-body text-sm text-muted-foreground">Add or manage employees</p>
                    <ChevronRight className="w-5 h-5 mt-4 text-muted-foreground group-hover:translate-x-2 transition-transform" />
                  </div>
                </Link>
                <Link to="/admin/reports" className="block">
                  <div className="bg-white border border-border p-8 hover-lift cursor-pointer group">
                    <BarChart3 className="w-8 h-8 mb-4 text-brand-gold" />
                    <h3 className="font-heading text-lg font-semibold mb-2">View Reports</h3>
                    <p className="font-body text-sm text-muted-foreground">Analytics and performance</p>
                    <ChevronRight className="w-5 h-5 mt-4 text-muted-foreground group-hover:translate-x-2 transition-transform" />
                  </div>
                </Link>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
